/* Which ways in the sign-in dialog offers.

   `GET /signin` decides, as it does on the whitelabel — a site with Telegram
   off gets no Telegram button. Its shape (platform
   `apis/public/src/v1/routes/signin/index.ts`):

     siwe, siws, magicLink, twitter   boolean
     telegram                         false | { username, botId }
     google                           false | { clientId, type: "oauth2" } | { type: "oauth2/pkce" }

   One thing the platform cannot know is whether this deployment can finish an
   OAuth sign-in. Google and X run in a popup on the API's own host and leave
   the session cookie there; the site sees it only when it talks to the API
   directly, which is the case on a casino inside the API's domain (the layout
   stamps `data-platform-api`, see direct.ts). Behind the proxy — a Vercel
   preview, local dev — the popup would sign the player in on the API host and
   the site would still show them signed out, so the two buttons are not
   offered there. Telegram posts JSON like magic link does and works either
   way. */

export type SignInMethods = {
  siwe?: boolean;
  siws?: boolean;
  magicLink?: boolean;
  twitter?: boolean;
  google?: false | { type: string; clientId?: string } | boolean;
  telegram?: false | { username: string; botId?: number } | boolean;
};

export type TelegramBot = { username: string; botId: number };

export type OfferedMethods = {
  magicLink: boolean;
  evm: boolean;
  solana: boolean;
  google: boolean;
  twitter: boolean;
  telegram: TelegramBot | null;
};

/* The bot the Login Widget authenticates against. The widget's popup is
   opened by bot id; a site that reports Telegram without one (an older
   platform) cannot run it, so it is treated as off. */
export const telegramBot = (methods: SignInMethods | null | undefined): TelegramBot | null => {
  const telegram = methods?.telegram;

  if (typeof telegram !== "object" || telegram === null) {
    return null;
  }

  const { username, botId } = telegram;

  return typeof username === "string" && username !== "" && typeof botId === "number" && botId > 0
    ? { username, botId }
    : null;
};

/* `telegramWidget` is false where Telegram refuses this host for the bot (its
   one BotFather domain is elsewhere — lib/telegram/bot-domain.ts, stamped by
   the layout as `data-telegram-widget="off"`); the Login Widget button is then
   not offered. The Mini App does not use the widget and is not affected. */
export const offeredMethods = (
  methods: SignInMethods | null | undefined,
  { oauth, telegramWidget = true }: { oauth: boolean; telegramWidget?: boolean },
): OfferedMethods => ({
  magicLink: methods?.magicLink === true,
  evm: methods?.siwe === true,
  solana: methods?.siws === true,
  google: oauth && Boolean(methods?.google),
  twitter: oauth && methods?.twitter === true,
  telegram: telegramWidget ? telegramBot(methods) : null,
});

/* What the layout stamped: false only when Telegram refused this host. */
export const telegramWidgetFromStamp = (stamp: string | undefined) => stamp !== "off";

/* Whether the dialog has anything below the email form to offer. */
export const hasSocialMethod = (offered: OfferedMethods) =>
  offered.google || offered.twitter || offered.telegram !== null;
